/**
 * SignDocument — Section 5.3 / 5.7 of the architecture blueprint.
 *
 * The client's e-signature surface for a TREC form. Reached at
 * `/trec/sign/{formId}` (the form UUID) by a logged-in client whose agent has
 * sent the form for signature from the admin Form Workspace.
 *
 * Signing model = DocuSign **embedded recipient via full redirect** (decided
 * 2026-06-27 — highest security, no iframe):
 *   1. We load the form (GET /trec/forms/{id}) and show the contract summary.
 *   2. "Review & sign" → POST /trec/forms/{id}/signing-view returns a short-
 *      lived hosted DocuSign URL; we redirect the whole tab to it.
 *   3. DocuSign redirects back to this page with `?event=signing_complete`
 *      (or cancel/decline/...). We poll the form status until the Connect
 *      webhook flips it to SIGNED, then show success + a signed-PDF download.
 *
 * Styling reuses the self-contained `.trec-`-prefixed SignDocument.css.
 */

import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import type { FormOut, FormStatus } from "@re-platform/api-client";
import { formService } from "@re-platform/api-client";
import { Button, Skeleton, StatusPill, type StatusTone } from "@re-platform/ui";

import { api } from "../../lib/api";
import { loadSession } from "../auth/session";

import "./SignDocument.css";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

function statusTone(status: FormStatus): StatusTone {
  if (status === "SIGNED") return "success";
  if (status === "SENT_FOR_SIGNATURE") return "info";
  return "neutral";
}

function statusLabel(status: FormStatus): string {
  switch (status) {
    case "DRAFT":
    case "PREFILLED":
      return "Being prepared";
    case "SENT_FOR_SIGNATURE":
      return "Ready to sign";
    case "SIGNED":
      return "Signed";
    default:
      return status;
  }
}

function fieldLabel(name: string): string {
  const s = name.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function SignDocument() {
  const { formId = "" } = useParams<{ formId: string }>();
  const [params] = useSearchParams();
  // DocuSign appends ?event=<outcome> to the return URL.
  const returnEvent = params.get("event");
  const justCompleted = returnEvent === "signing_complete";

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formQuery = useQuery({
    queryKey: ["sign-form", formId],
    // On the post-signing return, actively reconcile the status from DocuSign
    // (POST) instead of a plain GET — the authoritative Connect webhook is a
    // separate per-environment config and may be absent, which would otherwise
    // leave the client polling "Finalizing…" forever. Reconcile is idempotent.
    queryFn: () =>
      justCompleted
        ? formService.reconcileSignature(api, formId)
        : formService.get(api, formId),
    // Keep reconciling every few seconds until DocuSign reports completion.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.status === "SIGNED") return false;
      return justCompleted ? 2500 : false;
    },
  });
  const form = formQuery.data;

  async function startSigning() {
    setError(null);
    setStarting(true);
    try {
      const returnUrl = `${window.location.origin}/trec/sign/${formId}`;
      const { url } = await formService.signingView(api, formId, returnUrl);
      // Full top-level redirect — no iframe. DocuSign returns to returnUrl.
      window.location.assign(url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't start signing — please try again.",
      );
      setStarting(false);
    }
  }

  if (formQuery.isPending) {
    return (
      <div className="trec-page">
        <Skeleton variant="block" style={{ height: 80, marginBottom: 16 }} />
        <Skeleton variant="block" style={{ height: 320 }} />
      </div>
    );
  }

  if (formQuery.isError || !form) {
    return (
      <div className="trec-page">
        <header className="trec-head">
          <h1>Review &amp; sign</h1>
          <p>
            We couldn't load this document. It may have been withdrawn, or your
            session expired — <Link to="/login">sign in</Link> and try again.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="trec-page">
      <header className="trec-head">
        <h1>Review &amp; sign</h1>
        <p>
          TREC {form.form_type.replace(/_/g, " ")} · template{" "}
          <code>{form.template_version}</code>{" "}
          <StatusPill tone={statusTone(form.status)}>
            {statusLabel(form.status)}
          </StatusPill>
        </p>
      </header>

      {returnEvent &&
        returnEvent !== "signing_complete" &&
        form.status !== "SIGNED" && (
          <div className="trec-card" role="status" style={{ marginBottom: 16 }}>
            You exited signing without finishing (
            {returnEvent.replace(/_/g, " ")}). You can pick it back up whenever
            you're ready.
          </div>
        )}

      <div className="trec-card">
        <div className="trec-card__title">Document summary</div>
        <FieldSummary form={form} />
      </div>

      {form.status === "SIGNED" ? (
        <SignedPanel formId={formId} formType={form.form_type} />
      ) : form.status === "SENT_FOR_SIGNATURE" ? (
        justCompleted ? (
          <div className="trec-card" role="status">
            <strong>Finalizing your signature…</strong>
            <p style={{ marginBottom: 0 }}>
              Thanks — we're confirming with DocuSign. This page will update on
              its own in a few seconds.
            </p>
          </div>
        ) : (
          <div className="trec-card trec-sign">
            <p className="trec-sign__legal">
              You'll be taken to DocuSign to sign securely, then brought right
              back here. Your electronic signature is legally binding under the
              Texas UETA.
            </p>
            {error && (
              <p style={{ color: "var(--rp-color-danger, #dc2626)" }}>
                {error}
              </p>
            )}
            <Button onClick={startSigning} disabled={starting}>
              {starting ? "Opening DocuSign…" : "Review & sign with DocuSign"}
            </Button>
          </div>
        )
      ) : (
        <div className="trec-card" role="status">
          <strong>Almost ready</strong>
          <p style={{ marginBottom: 0 }}>
            Your agent is still preparing this document. We'll let you know the
            moment it's ready for your signature.
          </p>
        </div>
      )}
    </div>
  );
}

function FieldSummary({ form }: { form: FormOut }) {
  const filled = form.fields.filter((f) => (f.field_value ?? "").trim() !== "");
  if (filled.length === 0) {
    return (
      <p style={{ color: "var(--rp-color-text-muted)", margin: 0 }}>
        No details to preview yet.
      </p>
    );
  }
  return (
    <div className="trec-flist">
      {filled.map((f) => (
        <div key={f.field_name} className="trec-frow">
          <span className="trec-frow__label">{fieldLabel(f.field_name)}</span>
          <span className="trec-frow__page">{f.field_value}</span>
        </div>
      ))}
    </div>
  );
}

function SignedPanel({
  formId,
  formType,
}: {
  formId: string;
  formType: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The signed PDF is binary, so it can't go through the JSON ApiClient —
  // fetch it directly with the session bearer and stream it to a blob.
  async function download() {
    setError(null);
    setDownloading(true);
    try {
      const token = loadSession()?.accessToken;
      // No `credentials:"include"` — see the note in MyAgreements.tsx: a
      // wildcard Access-Control-Allow-Origin is refused in credentials mode,
      // so the browser threw the response away before the GET was ever sent.
      const resp = await fetch(`${API_BASE}/trec/forms/${formId}/signed-pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trec-${formType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't download the document.",
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="trec-card trec-success">
      <div className="trec-success__icon">✓</div>
      <h3 className="trec-success__title">Signed!</h3>
      <p className="trec-success__body">
        Thanks — your signature is recorded and all parties have a copy. Your
        agent has been notified.
      </p>
      {error && (
        <p style={{ color: "var(--rp-color-danger, #dc2626)" }}>{error}</p>
      )}
      <div className="trec-success__btns">
        <Button
          variant="secondary"
          size="sm"
          onClick={download}
          disabled={downloading}
        >
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
        <Link to="/">
          <Button variant="ghost" size="sm">
            Back to home
          </Button>
        </Link>
      </div>
    </div>
  );
}
